import instance, { ADMIN_PREFIX } from 'services/api';
import {
  PermissionRow,
  RoleDetail,
  RoleInput,
  RoleListRow
} from 'types/api/roles';

/**
 * GET /api/v2/roles — every role, flat.
 *
 * Roles have no hierarchy: /roles/groups and /roles/parent/:id are gone and
 * `parentId` is always null. This one call replaces the group-then-children
 * pair the pickers used to make.
 */
export const fetchRoles = async (): Promise<RoleListRow[]> => {
  const res = await instance.get<RoleListRow[]>(`${ADMIN_PREFIX}/roles`);

  return res.data ?? [];
};

/** GET /api/v2/roles/:id — role detail with rolePermissions hydrated. */
export const fetchRoleById = async (
  id: string | number
): Promise<RoleDetail> => {
  const res = await instance.get<RoleDetail>(`${ADMIN_PREFIX}/roles/${id}`);

  return res.data;
};

/**
 * GET /api/v2/admin/roles/permissions/:roleName — catalog Permission[]
 * with PermissionValue[] hydrated as `values`. Cycle-18 reshape.
 */
export const fetchPermissionsByRoleName = async (
  roleName: string
): Promise<PermissionRow[]> => {
  const res = await instance.get<{ data?: PermissionRow[] } | PermissionRow[]>(
    `${ADMIN_PREFIX}/roles/permissions/${roleName}`
  );

  // BE returns the array directly (per cycle-18 closure note); some legacy
  // tooling expects a {data: …} envelope, so accept both.
  if (Array.isArray(res.data)) return res.data;

  return (res.data?.data as PermissionRow[]) ?? [];
};

export const createRole = (input: RoleInput) =>
  instance.post<RoleDetail>(`${ADMIN_PREFIX}/roles`, input);

export const updateRole = (id: string | number, input: RoleInput) =>
  instance.put<RoleDetail>(`${ADMIN_PREFIX}/roles/${id}`, input);

export const deleteRole = (id: string | number) =>
  instance.delete<{ status: boolean }>(`${ADMIN_PREFIX}/roles/${id}`);
